import React from 'react';
import { Card, Space, Typography, Alert, Table, Tag, Statistic, Row, Col, Collapse, Empty, Badge, Tabs } from 'antd';
import {
  ApiOutlined,
  LinkOutlined,
  CheckOutlined,
  WarningOutlined,
  DashboardOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { DeployedItemData, DeployedResource } from '../types';
import { MethodBadge } from '../Common';
import { InfiniteFoot, useScrollBatch } from '@src/components/Common/DataList';
import JsonViewer from '@src/components/Common/JsonViewer';
import { FailureLogViewer } from './FailureLogViewer';
import { ObservabilityViewer } from './Observability';
import moment from 'moment';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

interface DeployedItemViewerProps {
  data: DeployedItemData;
}

/** how many more rows are drawn each time the reader reaches the end */
const RESOURCE_BATCH = 25;

/**
 * What the last execution deployed, a row per call. No page numbers: the rows
 * come in as the reader reaches them, the way every other list here does.
 */
const DeployedResourcesTable: React.FC<{ resources: any[]; columns: any[] }> = ({ resources, columns }) => {
  const { shown, hasMore, sentinelRef } = useScrollBatch({
    total: resources.length,
    batch: RESOURCE_BATCH,
  });

  return (
    <>
      <Table
        columns={columns}
        dataSource={resources.slice(0, shown)}
        rowKey="url"
        pagination={false}
        size="small"
        rowClassName={() => 'hover-row'}
        style={{
          '--hover-bg': '#fafafa'
        } as React.CSSProperties}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{
              padding: '20px',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
              borderRadius: '8px',
              border: '1px solid #e8e8e8'
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {/* Request Body */}
                {record.request_body && (
                  <div>
                    <Text strong style={{ fontSize: '13px', color: '#1890ff' }}>
                      Request Body:
                    </Text>
                    <div style={{
                      marginTop: '12px',
                      maxHeight: '300px',
                      overflow: 'auto',
                      borderRadius: '6px'
                    }}>
                      <JsonViewer data={record.request_body} fontSize="13px" lineHeight="1.6" />
                    </div>
                  </div>
                )}

                {/* Response Body */}
                {record.response_body && (
                  <div>
                    <Text strong style={{ fontSize: '13px', color: '#52c41a' }}>
                      Response Body:
                    </Text>
                    <div style={{
                      marginTop: '12px',
                      maxHeight: '300px',
                      overflow: 'auto',
                      borderRadius: '6px'
                    }}>
                      <JsonViewer data={record.response_body} fontSize="13px" lineHeight="1.6" />
                    </div>
                  </div>
                )}
              </Space>
            </div>
          ),
          rowExpandable: (record) => record.request_body !== null || record.response_body !== null,
        }}
      />

      {/* rows arrive as this comes into view — no page numbers */}
      <InfiniteFoot
        sentinelRef={sentinelRef}
        loading={false}
        loaded={shown}
        total={resources.length}
        hasMore={hasMore}
        noun="resource"
      />
    </>
  );
};

export const DeployedItemViewer: React.FC<DeployedItemViewerProps> = ({ data }) => {
  // Debug logging
  console.log('[DeployedItemViewer] Received data:', data);

  if (!data) {
    return (
      <Empty
        description="No deployed item data available"
        style={{ padding: '40px 0' }}
      />
    );
  }

  // Error boundary
  if (!data.last_execution || !data.deployed_resources) {
    return (
      <Alert
        message="Invalid Deployed Item Data Format"
        description={`Missing required fields. Available fields: ${Object.keys(data).join(', ')}`}
        type="error"
        showIcon
      />
    );
  }

  const { last_execution, deployed_resources, drift_detection_failures, failure_log, observability } = data;
  const isSuccess = last_execution.status === 'success';

  // Convert deployed_resources object to array for table
  const resourcesArray = Object.entries(deployed_resources).map(([url, resource]) => ({
    url,
    ...resource,
  }));

  // Table columns
  const columns = [
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 100,
      render: (method: string) => <MethodBadge method={method as any} />,
    },
    {
      title: 'Step Name',
      dataIndex: 'step_name',
      key: 'step_name',
      ellipsis: true,
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Endpoint URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url: string) => (
        <Text code style={{ fontSize: '11px' }}>
          <LinkOutlined style={{ marginRight: '4px' }} />
          {url}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'response_status',
      key: 'response_status',
      width: 100,
      render: (status: number) => (
        <Tag color={status < 300 ? 'success' : status < 400 ? 'warning' : 'error'}>
          HTTP {status}
        </Tag>
      ),
    },
    {
      title: 'Idempotent',
      dataIndex: 'idempotent',
      key: 'idempotent',
      width: 120,
      render: (idempotent: boolean) =>
        idempotent ? (
          <Tag icon={<CheckOutlined />} color="success">
            Yes
          </Tag>
        ) : (
          <Tag color="default">No</Tag>
        ),
    },
    {
      title: 'Deployed At',
      dataIndex: 'deployed_at',
      key: 'deployed_at',
      width: 180,
      render: (timestamp: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {moment(timestamp).format('YYYY-MM-DD HH:mm:ss')}
        </Text>
      ),
    },
  ];

  // Count failures for badge
  const failureCount = failure_log?.length || 0;
  const hasObservability = !!observability;

  return (
    <Card
      style={{
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        border: '1px solid #f0f0f0'
      }}
      bodyStyle={{ padding: '0' }}
    >
      <Tabs
        defaultActiveKey="deployed"
        style={{ padding: '0 16px' }}
        tabBarStyle={{ marginBottom: 0 }}
      >
        {/* Deployed Resources Tab */}
        <TabPane
          tab={
            <span>
              <ApiOutlined />
              Deployed Resources
              <Badge
                count={resourcesArray.length}
                style={{ marginLeft: 8, backgroundColor: '#1890ff' }}
                overflowCount={99}
              />
            </span>
          }
          key="deployed"
        >
            <div style={{ padding: '16px 0' }}>
              <DeployedResourcesTable resources={resourcesArray} columns={columns} />
            </div>
          </TabPane>

          {/* Observability Tab */}
          <TabPane
            tab={
              <span>
                <DashboardOutlined />
                Observability
                {hasObservability && observability?.overall_health && (
                  <Tag
                    color={
                      observability.overall_health === 'healthy' ? 'success' :
                      observability.overall_health === 'degraded' ? 'warning' : 'error'
                    }
                    style={{ marginLeft: 8, fontSize: '11px' }}
                  >
                    {observability.overall_health}
                  </Tag>
                )}
              </span>
            }
            key="observability"
          >
            <div style={{ padding: '16px 0' }}>
              {hasObservability ? (
                <ObservabilityViewer data={observability} />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No observability data collected yet"
                  style={{ padding: '40px 0' }}
                />
              )}
            </div>
          </TabPane>

          {/* Failure Log Tab */}
          <TabPane
            tab={
              <span>
                <ExclamationCircleOutlined />
                Failure Log
                {failureCount > 0 && (
                  <Badge
                    count={failureCount}
                    style={{ marginLeft: 8, backgroundColor: '#ff4d4f' }}
                    overflowCount={99}
                  />
                )}
              </span>
            }
            key="failures"
          >
            <div style={{ padding: '16px 0' }}>
              {failure_log && failure_log.length > 0 ? (
                <FailureLogViewer failureLog={failure_log} />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No failures recorded"
                  style={{ padding: '40px 0' }}
                />
              )}
            </div>
          </TabPane>

          {/* Last Execution Tab */}
          <TabPane
            tab={
              <span>
                <HistoryOutlined />
                Last Execution
                <Tag
                  color={isSuccess ? 'success' : 'error'}
                  style={{ marginLeft: 8, fontSize: '11px' }}
                >
                  {last_execution.status.toUpperCase()}
                </Tag>
              </span>
            }
            key="execution"
          >
            <div style={{ padding: '16px 0' }}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Execution Summary */}
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Status"
                      value={last_execution.status.toUpperCase()}
                      valueStyle={{ color: isSuccess ? '#52c41a' : '#ff4d4f', fontSize: '18px' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Pipeline Version"
                      value={last_execution.pipeline_version}
                      valueStyle={{ fontSize: '18px' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Completed Steps"
                      value={last_execution.completed_steps}
                      suffix={`/ ${last_execution.total_steps}`}
                      valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Failed Steps"
                      value={last_execution.failed_steps}
                      valueStyle={{ color: last_execution.failed_steps > 0 ? '#ff4d4f' : '#8c8c8c', fontSize: '18px' }}
                    />
                  </Col>
                </Row>

                {/* Timestamp and Message */}
                <div style={{ padding: '12px 16px', background: '#fafafa', borderRadius: '6px' }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>Executed at: </Text>
                      <Text style={{ fontSize: '13px' }}>
                        {moment(last_execution.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                      </Text>
                    </div>
                    {last_execution.message && (
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>Message: </Text>
                        <Text style={{ fontSize: '13px' }}>{last_execution.message}</Text>
                      </div>
                    )}
                  </Space>
                </div>

                {/* Drift Detection Failures */}
                {drift_detection_failures && drift_detection_failures.length > 0 && (
                  <Alert
                    message={`Drift Detection Failures (${drift_detection_failures.length})`}
                    description={
                      <Collapse ghost>
                        <Panel header="View Details" key="drift">
                          <JsonViewer data={drift_detection_failures} fontSize="13px" lineHeight="1.6" />
                        </Panel>
                      </Collapse>
                    }
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                  />
                )}
              </Space>
            </div>
          </TabPane>
      </Tabs>

      <style>{`
        .hover-row:hover {
          background-color: #fafafa !important;
          transition: background-color var(--netorca-dur-mid) var(--netorca-ease-standard);
        }
      `}</style>
    </Card>
  );
};

export default DeployedItemViewer;
